// ========== React & Libraries ==========
import { z } from 'zod';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import { getCoreRowModel, useReactTable } from '@tanstack/react-table';

// ========== Commons ==========
import {
  coachPaymentKind,
  coachPaymentKindToLabel,
  coachPaymentStatusKind,
  coachPaymentStatusToLabel,
  coachPaymentStatusToColor,
  CoachPaymentReport,
  coachPaymentReportSchema,
  CoachPaymentNoteToLabel,
  coachPaymentDepositInfoStatusSchema,
  CoachPaymentDepositInfoStatusToLabel,
  coachPaymentNoteKind,
} from '@airreading/common/data';

// ========== Utils & Helpers ==========
import { bulkSendTeacherPayment, getOngoingPayments, getTotalSumFromLifetime } from '@/lib/api-calls';
import { validateRouteRole } from '@/lib/routing';

import { paginationRequestSchema } from '@/lib/pagination';

// ========== Table Components ==========
import { createTableRowSelectionCell, EntityTable } from '@/components/table/entity-table';
import { TableErrorState } from '@/components/table/table-error-state';

// ========== Layout Components ==========
import { InfoPage } from '@/components/info-page';

// ========== Users/Teachers Components ==========
import { coachesSectionTabs } from '../-components/tabs';
import { isAirAdmin, isNotNullish, plainDateFromStringOrElseUndefined } from '@airreading/common';

import { airColumnHelper } from '@/lib/tables';
import { formatCurrency } from '@/lib/common-utils';
import { usePaginatedFilters } from '@/lib/filtering';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { TableFilterInputField } from '@/components/table/table-filter-field';
import { TableFilterDropdown } from '@/components/table/table-filter-dropdown';
import { TableFilterPlainDatePicker } from '@/components/table/table-filter-plain-date-picker';
import { TableFilterResetButton } from '@/components/table/table-filter-reset-button';
import { usePayCycleOptions } from '@/lib/api-call-hooks';
import { useToast } from '@/components/ui/hooks/use-toast';
import { CreatePayment } from '../-components/payments/create-payment';
import { KebabOptions } from '../-components/payments/kebab';
import { CoachPaymentKindTag } from '../-components/payments/coach-payment-kind-tag';
import { FromOptions } from '../-components/payments/from-options';
import { Badge } from '@/components/ui/badge';
import { DropdownMenuItem } from '@/components/ui/dropdown-menu';
import { useRowsBulkSelection } from '@/lib/bulk-selection';
import { BuildToastResponse } from './-build-toast-response';
import { TableMoreFiltersMenu } from '@/components/table/table-more-filters-menu';
import { PaymentInstrumentModal } from '../-components/payments/payment-instrument-modal';
import { TransferDetailsModal } from '../-components/payments/transfer-details-modal';
import { DepositInfoStatus } from '../-components/payments/deposit-info-status';

const payCycleRequestSchema = paginationRequestSchema.extend({
  query: z.string().optional().catch(''),
  type: z.array(coachPaymentKind).optional(),
  payDate: z.string().optional(),
  payCycleInterval: z.string().optional(),
  paymentSentStatus: z.array(coachPaymentStatusKind).optional(),
  depositInfoStatus: z.array(coachPaymentDepositInfoStatusSchema).optional(),
  notes: z.array(coachPaymentNoteKind).optional(),
});

const filterableCoachPaymentStatusKind = coachPaymentStatusKind.extract(['not_started', 'processing', 'processed']);

const columnHelper = airColumnHelper<CoachPaymentReport>(coachPaymentReportSchema.keyof());

const coachPaymentReportTableColumns = [
  createTableRowSelectionCell(columnHelper),

  columnHelper.accessor('coach', {
    id: 'fullNameOrEmail',
    header: 'Name',
    cell: ({ getValue }) => {
      const coach = getValue();
      return <Link to={`/teachers/${coach.id}`}>{coach.fullNameOrEmail}</Link>;
    },
  }),

  columnHelper.accessor('totalAmountInCents', {
    id: 'totalAmount',
    header: 'Total amount',
    cell: ({ getValue }) => {
      const totalAmountCent = getValue();
      return formatCurrency(totalAmountCent / 100);
    },
  }),

  columnHelper.accessor('payPeriodInterval', {
    id: 'payPeriod',
    header: 'Pay cycle',
    cell: ({ getValue }) => getValue(),
  }),

  columnHelper.accessor('payDate', {
    id: 'paymentDate',
    header: 'Pay date',
    cell: ({ getValue }) => getValue(),
  }),

  columnHelper.accessor('paymentStatus', {
    id: 'paymentStatus',
    header: 'Pay sent',
    cell: ({ getValue }) => {
      const status = getValue();

      return (
        <Badge
          variant="secondary"
          style={{
            color: coachPaymentStatusToColor[status ?? coachPaymentStatusKind.enum.not_started],
          }}
          className="bg-transparent  text-sm hover:bg-transparent focus:bg-transparent focus-visible:bg-transparent relative pl-0"
        >
          <span
            className="w-[10px] h-[10px] rounded-full mr-1"
            style={{ backgroundColor: coachPaymentStatusToColor[status ?? coachPaymentStatusKind.enum.not_started] }}
          />
          {coachPaymentStatusToLabel[status] ?? coachPaymentStatusToLabel[coachPaymentStatusKind.enum.not_started]}
        </Badge>
      );
    },
    enableSorting: false,
  }),

  columnHelper.accessor('type', {
    id: 'paymentType',
    header: 'Payment type',
    cell: ({ getValue }) => {
      return <CoachPaymentKindTag kind={getValue()} />;
    },
  }),

  columnHelper.accessor((row) => row, {
    id: 'totalSessions',
    header: 'Total sessions taught',
    cell: ({ getValue }) => {
      const report = getValue();

      if (report.type !== coachPaymentKind.enum.teaching) return;

      return report.totalSessionsTaught;
    },
  }),

  columnHelper.accessor('depositInfoStatus', {
    id: 'depositInfoStatus',
    header: 'Deposit info status',
    cell: ({ getValue }) => {
      const depositInfoStatus = getValue();

      return <DepositInfoStatus depositInfoStatus={depositInfoStatus} />;
    },
  }),

  columnHelper.accessor((row) => row, {
    id: 'paymentInstrumentId',
    header: 'Brex - Payment Instrument ID',
    cell: ({ getValue }) => {
      const { coach, depositInfoStatus } = getValue();

      const { paymentInstrumentId } = coach;

      return <PaymentInstrumentModal paymentInstrumentId={paymentInstrumentId} depositInfoStatus={depositInfoStatus} />;
    },
  }),

  columnHelper.accessor('paySentId', {
    id: 'paySentId',
    header: 'Brex - Pay Sent ID',
    cell: ({ getValue }) => {
      const paySendId = getValue();

      return <TransferDetailsModal paySentId={paySendId} />;
    },
  }),

  columnHelper.accessor((row) => row, {
    id: 'notes',
    header: 'Notes',
    cell: ({ getValue }) => {
      const report = getValue();
      return report.notes.map((note) => CoachPaymentNoteToLabel[note]).join(', ');
    },
  }),

  columnHelper.accessor('coach.email', {
    id: 'email',
    header: 'Email',
    cell: ({ getValue }) => getValue(),
  }),

  columnHelper.accessor((row) => row, {
    id: 'kebabOptions',
    header: '',
    cell: ({ getValue }) => {
      const report = getValue();
      return <KebabOptions {...report} from={FromOptions.teacherPay} teacherId={report.coach.id} />;
    },
  }),
];

export const Route = createFileRoute('/_layout/teachers/teacher-pay/')({
  beforeLoad: validateRouteRole(isAirAdmin),
  component: TeacherPay,
  validateSearch: payCycleRequestSchema,
});

function TeacherPay() {
  const [isSelectAllPagesActive, setIsSelectAllPagesActive] = useState(false);

  const queryClient = useQueryClient();

  const {
    pageSize,
    pageIndex,
    onPaginationChange,
    filters: {
      query: [[nameQuery, activeNameQuery], setNameQuery],
      type: [[paymentKind, activePaymentKind], setPaymentKind],
      payDate: [[payDate, activePayDate], setPayDate],
      payCycleInterval: [[payCycleInterval, activePayCycleInterval], setPayCycleInterval],
      paymentSentStatus: [[paymentSentStatus, activePaymentSentStatus], setPaymentSentStatus],
      depositInfoStatus: [[depositInfoStatus, activeDepositInfoStatus], setDepositInfoStatus],
      notes: [[notes, activeNotes], setNotes],
    },
    resetFilters,
    activeFiltersCount,
  } = usePaginatedFilters(Route.id, [
    'query',
    'type',
    'payDate',
    'payCycleInterval',
    'paymentSentStatus',
    'depositInfoStatus',
    'notes',
  ]);

  const { toast } = useToast();

  const { data, isPending, refetch, error } = useQuery(
    getOngoingPayments({
      pageIndex,
      pageSize,
      filters: {
        query: activeNameQuery,
        type: activePaymentKind,
        payDate: plainDateFromStringOrElseUndefined(activePayDate)?.toString(),
        payCycleInterval: activePayCycleInterval,
        paymentSentStatus: activePaymentSentStatus,
        depositInfoStatus: activeDepositInfoStatus,
        notes: activeNotes,
      },
    }),
  );

  const { data: totalSum } = useQuery(
    getTotalSumFromLifetime({
      filters: {
        payCycleInterval: activePayCycleInterval,
        payDateStartOnOrBeforePlainDate: plainDateFromStringOrElseUndefined(activePayDate),
        payDateStartOnOrAfterPlainDate: plainDateFromStringOrElseUndefined(activePayDate),
      },
    }),
  );

  const { mutateAsync: bulkSendMutate } = useMutation({
    mutationFn: bulkSendTeacherPayment,
  });

  const { rowSelection, getRowId, onRowSelectionChange, rowDataSelection, resetAllRowSelection } =
    useRowsBulkSelection<CoachPaymentReport>({
      getRowId: (row) => row.id,
      rowIdToData: (id) => data?.payments.find((session) => session.id === id),
    });

  // Handle isSelectAllPagesActive changes and reset selections when deselecting all
  const handleSelectAllPagesChange = useCallback(
    (isActive: boolean) => {
      setIsSelectAllPagesActive(isActive);
      if (!isActive) {
        // When deselecting all pages, reset all row selections
        resetAllRowSelection();
      }
    },
    [resetAllRowSelection],
  );

  useEffect(() => {
    if (!isSelectAllPagesActive || !data?.payments || data.payments.length === 0) {
      return;
    }
    const allRowIds = data.payments.reduce(
      (acc, payment) => {
        acc[payment.id] = true;
        return acc;
      },
      {} as Record<string, boolean>,
    );
    onRowSelectionChange(allRowIds);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, isSelectAllPagesActive]);

  // Reset isSelectAllPagesActive when filters change
  useEffect(() => {
    if (!isSelectAllPagesActive) {
      return;
    }
    setIsSelectAllPagesActive(false);
    resetAllRowSelection();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nameQuery, paymentKind, payDate, payCycleInterval]);

  const onSendPay = useCallback(
    async (data: CoachPaymentReport[]) => {
      if (!isSelectAllPagesActive) {
        const response = await bulkSendMutate({
          coachPaymentIds: data.map((payment) => payment.id),
        });

        toast({
          title: 'Pay sent',
          description: <BuildToastResponse response={response} />,
        });
      }
      if (isSelectAllPagesActive) {
        const response = await bulkSendMutate({
          query: activeNameQuery,
          type: activePaymentKind,
          payDate: plainDateFromStringOrElseUndefined(activePayDate)?.toString(),
          payCycleInterval: activePayCycleInterval,
        });

        toast({
          title: 'Pay sent',
          description: <BuildToastResponse response={response} />,
        });
      }

      queryClient.invalidateQueries(getOngoingPayments());
    },
    [
      isSelectAllPagesActive,
      toast,
      queryClient,
      bulkSendMutate,
      activeNameQuery,
      activePaymentKind,
      activePayDate,
      activePayCycleInterval,
    ],
  );

  const bulkActionsMenuItems = useMemo(
    () => (
      <>
        <DropdownMenuItem
          onSelect={(e) => {
            e.preventDefault();
            onSendPay(rowDataSelection);
          }}
        >
          Send pay
        </DropdownMenuItem>
      </>
    ),
    [onSendPay, rowDataSelection],
  );

  const payCycleOptions = usePayCycleOptions();

  const table = useReactTable({
    data: data?.payments ?? [],
    columns: coachPaymentReportTableColumns,
    getRowId,
    rowCount: data?.total ?? 0,
    getCoreRowModel: getCoreRowModel(),
    onRowSelectionChange,
    onPaginationChange,
    manualPagination: true,
    state: {
      rowSelection,
      pagination: {
        pageIndex,
        pageSize,
      },
    },
  });

  const changeFilter = (filter: 'payDate' | 'payCycleInterval', value: unknown) => {
    if (filter === 'payDate') {
      setPayDate(value as string);
      setPayCycleInterval(undefined);
    }
    if (filter === 'payCycleInterval') {
      setPayCycleInterval(value as string);
      setPayDate(undefined);
    }
    if ((activePayDate && filter === 'payCycleInterval') || (activePayCycleInterval && filter === 'payDate')) {
      toast({
        title: 'Pay date and pay cycle interval must be selected',
        description: 'Please select a pay date and a pay cycle interval',
      });
    }
  };

  const filterComponents = useMemo(
    () => (
      <>
        <TableFilterInputField
          placeholder="Search name or email"
          value={nameQuery ?? ''}
          onChange={(e) => setNameQuery(e.target.value)}
        />
        <TableFilterDropdown
          title="Payment type"
          options={Object.values(coachPaymentKind.enum).map((kind) => ({
            label: coachPaymentKindToLabel[kind],
            value: kind,
          }))}
          selectedValues={paymentKind ?? []}
          onChange={setPaymentKind}
        />
        <TableFilterPlainDatePicker
          selectedPlainDateString={payDate}
          onPlainDateSelect={(date) => {
            changeFilter('payDate', date?.toString());
          }}
          tableFilterDatePickerProps={{
            placeholder: 'Pay date',
          }}
        />
        <TableFilterDropdown
          title="Pay cycle"
          singleSelect
          options={payCycleOptions}
          selectedValues={[payCycleInterval].filter(isNotNullish)}
          onChange={(selected) => {
            changeFilter('payCycleInterval', selected.filter((v) => v !== payCycleInterval)[0]);
          }}
        />
        <TableMoreFiltersMenu
          activeFiltersCount={activeFiltersCount}
          filterItems={[
            {
              title: 'Pay sent status',
              filterComponent: (
                <TableFilterDropdown
                  title="Pay sent status"
                  options={filterableCoachPaymentStatusKind.options.map((status) => ({
                    label: coachPaymentStatusToLabel[status],
                    value: status,
                  }))}
                  selectedValues={paymentSentStatus ?? []}
                  onChange={setPaymentSentStatus}
                />
              ),
            },
            {
              title: 'Deposit info status',
              filterComponent: (
                <TableFilterDropdown
                  title="Deposit info status"
                  options={coachPaymentDepositInfoStatusSchema.options.map((status) => ({
                    label: CoachPaymentDepositInfoStatusToLabel[status],
                    value: status,
                  }))}
                  selectedValues={depositInfoStatus ?? []}
                  onChange={setDepositInfoStatus}
                />
              ),
            },
            {
              title: 'Payment notes',
              filterComponent: (
                <TableFilterDropdown
                  title="Payment notes"
                  options={coachPaymentNoteKind.options.map((status) => ({
                    label: CoachPaymentNoteToLabel[status],
                    value: status,
                  }))}
                  selectedValues={notes ?? []}
                  onChange={setNotes}
                />
              ),
            },
          ]}
        />
        <TableFilterResetButton show={true} onClick={resetFilters} />
      </>
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      nameQuery,
      paymentKind,
      setPaymentKind,
      payDate,
      setPayDate,
      payCycleOptions,
      payCycleInterval,
      setPayCycleInterval,
      resetFilters,
      setNameQuery,
    ],
  );

  return (
    <InfoPage headerText="Teachers" tabs={coachesSectionTabs()}>
      <EntityTable
        table={table}
        showPagination
        filters={{ filterLeftComponents: filterComponents }}
        topBarRightComponents={<CreatePayment from={FromOptions.teacherPay} />}
        bulkActionsMenuItems={bulkActionsMenuItems}
        isLoading={isPending}
        hasError={!!error}
        emptyState="No payments found"
        errorState={<TableErrorState refetchAction={refetch} />}
        isSelectAllPagesActive={isSelectAllPagesActive}
        onSelectAllPagesChange={handleSelectAllPagesChange}
        totalRowCount={data?.total}
        topBarBottomComponents={
          <p className="text-sm font-semibold">
            SUM <span className="font-bold text-2xl">{formatCurrency((totalSum ?? 0) / 100)}</span>
          </p>
        }
      />
    </InfoPage>
  );
}
